export const SDK_ENVIRONMENTS = ['production', 'preview', 'local', 'disabled'] as const;

export type SdkEnvironment = (typeof SDK_ENVIRONMENTS)[number];

export interface EnvironmentInput {
  /** `window.location` of the game document. */
  location: { hostname: string; protocol: string; search: string; origin?: string };
  /** True when the document is nested in another window. */
  embedded: boolean;
  /** Set by the host wrapper on the session message; absent outside Riz. */
  hostDeclared?: SdkEnvironment | null;
  /** Sitelock is opt-in per game (SDK v2 plan §4.1); off means never `disabled`. */
  sitelockEnabled?: boolean;
}

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]', '0.0.0.0']);

export function isLocalHost(hostname: string, protocol: string): boolean {
  if (protocol === 'file:') return true;
  if (LOCAL_HOSTNAMES.has(hostname)) return true;
  return hostname.endsWith('.localhost');
}

export function hasLocalOverride(search: string): boolean {
  if (!search) return false;
  try {
    return new URLSearchParams(search).get('rizLocalSdk') === 'true';
  } catch {
    return false;
  }
}

/**
 * Resolved before the handshake, then refined by it: only the host can confirm
 * `production` versus `preview`, and only a sitelocked game can reach
 * `disabled`. An unembedded document is local rather than disabled, so opening
 * a build straight from disk keeps working.
 */
export function resolveEnvironment(input: EnvironmentInput): SdkEnvironment {
  const { location, embedded, hostDeclared, sitelockEnabled } = input;

  if (hasLocalOverride(location.search)) return 'local';
  if (isLocalHost(location.hostname, location.protocol)) return 'local';
  if (hostDeclared === 'preview' || hostDeclared === 'production') return hostDeclared;
  if (!embedded) return sitelockEnabled ? 'disabled' : 'local';
  return sitelockEnabled && hostDeclared === undefined ? 'disabled' : 'local';
}

export function isLiveEnvironment(environment: SdkEnvironment): boolean {
  return environment === 'production' || environment === 'preview';
}
